<template>
  <v-container fluid>
    <div class="d-flex flex-column flex-md-row justify-space-between">
      <div>
        <h1 class="title">{{ $t('SHIPPING_PAGE.TITLE.SHIPPING') }}</h1>
      </div>
      <div class="transporter-logo">
        <img
          v-if="currentExpressistTransporter?.transporter_type === 'CHRONOPOST'"
          alt="chronopost-svg"
          src="../../../../assets/icons/file/chronopost.svg"
        />
      </div>
      <div>
        <h1 class="title">{{ 'N°' + currentRepairFile?.internal_command_number }}</h1>
      </div>
    </div>

    <v-form ref="formShipping" @submit.prevent="submit">
      <v-row>
        <v-col>
          <v-card
            v-if="departure"
            class="rounded-xl mt-8"
            :class="constraintError ? 'error-border' : ''"
            :border="false"
            :elevation="0"
          >
            <h3 class="font-weight-bold">{{ $t('SHIPPING_PAGE.DEPARTURE') }}</h3>

            <p class="mb-0 error" v-if="constraintError">
              {{ $t('ERROR.INVALID_CONSTRAINTS_ERROR_KEY') }}
            </p>

            <p class="mb-0">{{ departure.address_name }}</p>
            <p class="mb-0">{{ departure.address_number + ' ' + departure.address_street }}</p>
            <p class="mb-0">{{ departure.address_zip_code + ' ' + departure.address_city }}</p>

            <div v-if="constraintError" class="update-departure-btn mt-2">
              <v-btn
                :loading="loading"
                class="pl-15 pr-15"
                :class="loading ? 'boom-button-transparent' : 'boom-button'"
                variant="outlined"
                @click="openUpdateDepartureAddressModal()"
              >
                {{ $t('COMMON.BUTTON.UPDATE') }}
              </v-btn>
            </div>
          </v-card>
        </v-col>
        <v-col>
          <v-card v-if="delivery" class="rounded-xl mt-8" :border="false" :elevation="0">
            <h3 class="font-weight-bold">{{ $t('SHIPPING_PAGE.DELIVERY') }}</h3>
            <p class="mb-0">{{ delivery.address_name }}</p>
            <p class="mb-0">{{ delivery.address_number + ' ' + delivery.address_street }}</p>
            <p class="mb-0">{{ delivery.address_zip_code + ' ' + delivery.address_city }}</p>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-card class="rounded-xl mt-8" :border="false" :elevation="0">
            <h3 class="font-weight-bold">{{ $t('SHIPPING_PAGE.PACKAGE') }}</h3>
            <p class="mb-0">
              {{ $t('COMMON.WEIGHT') }}:
              {{
                currentRepairFile
                  ? currentRepairFile.packet_weight.toFixed(3) + $t('COMMON.KILOGRAM')
                  : ''
              }}
            </p>
            <p class="mb-0">
              {{ $t('COMMON.WIDTH') }}:
              {{
                currentRepairFile
                  ? (currentRepairFile.packet_width / 10).toFixed(1) + $t('COMMON.CENTIMETER')
                  : ''
              }}
            </p>
            <p class="mb-0">
              {{ $t('COMMON.LENGTH') }}:
              {{
                currentRepairFile
                  ? (currentRepairFile.packet_length / 10).toFixed(1) + $t('COMMON.CENTIMETER')
                  : ''
              }}
            </p>
          </v-card>
        </v-col>

        <v-col v-if="chronopostConstraints">
          <v-card v-if="delivery" class="rounded-xl mt-8" :border="false" :elevation="0">
            <v-row class="mb-0">
              <v-col cols="12" md="4" class="chronopost-constraint-title">
                <h3 class="font-weight-bold">{{ $t('SHIPPING_PAGE.CHRONOPOST_CONSTRAINTS') }}</h3>
              </v-col>
              <v-col cols="12" md="8">
                <div class="collect-status">
                  <p class="mb-0 pb-0">{{ $t('SHIPPING_PAGE.COLLECT_SUBTITLE') }}</p>
                  <v-checkbox
                    class="checkbox-custom"
                    density="compact"
                    hide-details
                    v-model="selectWithoutCollectStatus"
                    color="#559a1b"
                  />
                </div>
              </v-col>
            </v-row>

            <v-row v-if="!selectWithoutCollectStatus" class="mt-0 pt-0">
              <v-col class="mb-0 pt-0">
                <p class="mb-0">
                  {{ $t('SHIPPING_PAGE.EARLY_COLLECT') }}:
                  {{ chronopostConstraints.earliest_arrival_time }}
                </p>
                <p class="mb-0">
                  {{ $t('SHIPPING_PAGE.LAST_COLLECT') }}:
                  {{ chronopostConstraints.hour_max_for_collect }}
                </p>
                <p class="mb-0">
                  {{ $t('SHIPPING_PAGE.MAX_CREATION') }}:
                  {{ chronopostConstraints.esd_creation_request_deadline }}
                </p>
              </v-col>
            </v-row>

            <v-row v-if="!selectWithoutCollectStatus" class="mt-2">
              <v-col cols="9">
                <p class="mb-0">{{ $t('SHIPPING_PAGE.SHIPPING_DAY') }}:</p>
                <div class="d-flex align-center">
                  <v-menu :close-on-content-click="false">
                    <template v-slot:activator="{ props }">
                      <img
                        v-bind="props"
                        alt="export"
                        class="date-picker"
                        src="@/assets/icons/repair-file-list/date.svg"
                        @click="showValidDate = true"
                      />
                      <v-text-field
                        v-bind="props"
                        class="ml-3 date-creation"
                        v-model="collectDate"
                        :value="formatDate()"
                        readonly
                        hide-details
                        @click="showValidDate = true"
                      />
                      <v-icon
                        icon="mdi-check-circle-outline"
                        color="#767676"
                        class="update-icon"
                        v-if="showValidDate"
                        @click="showValidDate = false"
                      />
                    </template>
                    <v-locale-provider locale="fr">
                      <v-date-picker
                        v-model="collectDate"
                        hide-header
                        color="#559A1B"
                        locale="fr"
                        :min="minDate"
                        @focusin="showValidDate = true"
                      />
                    </v-locale-provider>
                  </v-menu>
                </div>
              </v-col>

              <v-col cols="3">
                <v-btn
                  :disabled="loading || !selectedChronopostService || !collectDate"
                  :loading="loading"
                  :class="loading ? 'boom-button-transparent' : 'boom-button'"
                  variant="outlined"
                  @click="checkFeasibility()"
                >
                  {{ $t('SHIPPING_PAGE.BUTTON.FEASIBILITY') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col cols="12" class="d-flex justify-start">
          <v-btn
            :disabled="loading"
            :loading="loading"
            class="mr-1 pl-15 pr-15"
            :class="loading ? 'boom-button-transparent' : 'boom-button'"
            @click="backToStepper()"
            variant="outlined"
          >
            {{ $t('COMMON.BUTTON.BACK') }}
          </v-btn>

          <v-btn
            :disabled="loading || !selectedChronopostService || !collectDate"
            :loading="loading"
            class="pl-15 pr-15"
            :class="loading ? 'boom-button-transparent' : 'boom-button'"
            type="submit"
            variant="outlined"
          >
            {{ $t('SHIPPING_PAGE.BUTTON.GENERATE_SEND') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <AddressModal ref="addressModal" />
  </v-container>
</template>

<script>
import RepairFileService from '@/services/RepairFileService'
import ExpressistTransporterService from '@/services/transporters/ExpressistTransporterService'
import ChronopostService from '@/services/transporters/ChronopostService'
import {
  displayToast,
  handleResponseError,
  shouldShowTooltip
} from '@/components/utils/globalFunction'
import DeliveryAddressTypeConstant from '@/constants/deliveryAddressTypeConstant'
import AddressModal from '@/components/modal/repair-file/AddressModal.vue'
import repairFileService from '@/services/RepairFileService'

export default {
  name: 'ShippingPage',
  components: {
    AddressModal
  },
  data() {
    const date = new Date()
    date.setDate(date.getDate())
    date.setHours(0, 0, 0, 0)

    return {
      loading: false,
      repairFileBoomerangId: null,
      currentRepairFile: null,
      currentExpressistTransporter: null,
      chronopostConstraints: null,

      departure: null,
      delivery: null,

      selectedChronopostService: null,
      chronopostServiceList: [],

      collectDate: date,
      minDate: null,

      showValidDate: false,

      selectWithoutCollectStatus: false,

      error: null,

      constraintError: false
    }
  },
  async created() {
    this.repairFileBoomerangId = this.$route.params.repairFileBoomerangId
    this.currentRepairFile = await RepairFileService.get(this.repairFileBoomerangId)

    this.currentExpressistTransporter =
      await ExpressistTransporterService.getExpressistTransporterById(
        this.currentRepairFile.initial_expressist_transporter_id
      )

    this.currentRepairFile.delivery_address_list.forEach((address) => {
      if (address.type === 'DEPARTURE') {
        this.departure = address
      }
      if (address.type === 'DELIVERY') {
        this.delivery = address
      }
    })

    if (this.currentExpressistTransporter?.transporter_type === 'CHRONOPOST') {
      const expressistConnexionServiceListWrapper =
        await ExpressistTransporterService.getExpressistConnexionServiceList(
          this.currentRepairFile.initial_expressist_transporter_connexion_id
        )

      this.chronopostServiceList = expressistConnexionServiceListWrapper.item_list
      // Force selection of Chronopost service to unique service configured.
      // This is hack asked by Boomerang with
      // this ticket: https://leviatan.myjetbrains.com/youtrack/issue/BOOM-1365/EVO-Chronopost-Portail
      // Rollback change of 31/01/2025 on this file for use correctly the selection of Chronopost service.
      this.selectedChronopostService = this.chronopostServiceList[0].code

      await this.checkAndGetChronopostConstraints()
    } else {
      const timeElapsed = Date.now()
      const today = new Date(timeElapsed)
      this.minDate = today.toISOString()
    }
  },
  methods: {
    shouldShowTooltip,
    formatDate() {
      const date = this.collectDate
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    async submit() {
      this.loading = true
      try {
        await ChronopostService.generateShipment(
          this.currentRepairFile.technical_id,
          this.collectDate,
          this.selectedChronopostService,
          !this.selectWithoutCollectStatus
        )

        displayToast(this.$t('SHIPPING_PAGE.MESSAGE.SUCCESS.GENERATE_MESSAGE'), 'success')
        this.$router.push('/repair-file/finalization/' + this.currentRepairFile.boomerang_id)
      } catch (error) {
        const key = error.response?.data?.key || ''
        const message = error.response?.data?.message || ''
        const status = error.response?.status || 'UNKNOWN'

        if (key === '21' || key === '13') {
          this.error = this.$t(message || 'ERROR.CHRONOPOST_GENERIC_ERROR_KEY')
        } else if (key === 'collect_date' || key === 'hour_max_for_collect_already_reached') {
          this.error = this.$t('ERROR.TRANSPORTER_COLLECT_ERROR_KEY')
        } else if (!isNaN(Number(key)) || /^[a-z]+(_[a-z]+)+$/.test(key) || key.length > 0) {
          this.error = this.$t('ERROR.CHRONOPOST_GENERIC_ERROR_KEY')
        } else {
          this.error = this.$t('ERROR_STATUS_CODE_MESSAGE.' + status)
        }

        displayToast(this.error, 'error')
      } finally {
        this.loading = false
      }
    },
    async checkFeasibility() {
      this.loading = true
      try {
        await ChronopostService.checkFeasibility(
          this.currentRepairFile.technical_id,
          this.collectDate
        )

        displayToast(this.$t('SHIPPING_PAGE.MESSAGE.SUCCESS.FEASIBILITY_MESSAGE'), 'success')
      } catch (error) {
        if (error.response.data !== null) {
          switch (error.response.data.key) {
            case '21':
            case '13':
              this.error = error.response.data.message
              break
            case 'hour_max_for_collect_already_reached':
            case 'collect_date':
              this.error = this.$t('ERROR.TRANSPORTER_COLLECT_ERROR_KEY')
              break
            default:
              this.error = this.$t('ERROR_STATUS_CODE_MESSAGE.' + error.response.status)
          }
        }

        displayToast(this.error, 'error')
      } finally {
        this.loading = false
      }
    },
    async checkAndGetChronopostConstraints() {
      const timeElapsed = Date.now()
      const today = new Date(timeElapsed)

      try {
        this.chronopostConstraints = await ChronopostService.getConstraints(
          this.currentRepairFile.technical_id
        )

        const esd_creation_request_deadline_split =
          this.chronopostConstraints.esd_creation_request_deadline.split(':')
        const esd_creation_request_deadline_hours = esd_creation_request_deadline_split[0]
        const esd_creation_request_deadline_minutes = esd_creation_request_deadline_split[1]

        if (today.getHours() > parseInt(esd_creation_request_deadline_hours)) {
          this.minDate = today.toISOString()
        } else if (
          today.getHours() === parseInt(esd_creation_request_deadline_hours) &&
          today.getMinutes() > parseInt(esd_creation_request_deadline_minutes)
        ) {
          today.setDate(today.getDate() - 1)
          this.minDate = today.toISOString()
        } else {
          today.setDate(today.getDate() - 1)
          this.minDate = today.toISOString()
        }
      } catch (error) {
        handleResponseError(error)
        this.minDate = today.toISOString()
      }
    },
    async openUpdateDepartureAddressModal() {
      let departureAddress = this.currentRepairFile.delivery_address_list.find((address) => {
        return address.type === DeliveryAddressTypeConstant.DEPARTURE
      })

      let departureAddressFormated = {
        name: departureAddress.address_name,
        number: departureAddress.address_number,
        street: departureAddress.address_street,
        complementOne: departureAddress.address_complement_1,
        complementTwo: departureAddress.address_complement_2,
        zipCode: departureAddress.address_zip_code,
        city: departureAddress.address_city,
        contactName: departureAddress.contact_name,
        contactEmail: departureAddress.contact_email,
        contactPhone: departureAddress.contact_phone
      }

      const response = await this.$refs.addressModal.show({
        addressInfo: departureAddressFormated,
        title: this.$t('SHIPPING_PAGE.UPDATE_DEPARTURE')
      })
      if (response.confirmed) {
        try {
          this.loading = true

          let address_updated = await repairFileService.updateRepairFileDepartureAddress(
            departureAddress.technical_id,
            response.addressInfo
          )
          await this.checkAndGetChronopostConstraints()

          this.departure = address_updated
          this.constraintError = false
        } catch (error) {
          handleResponseError(error)
        } finally {
          this.loading = false
        }
      }
    },
    async backToStepper() {
      try {
        this.loading = true

        await repairFileService.backToStepper(this.currentRepairFile.technical_id)

        this.$router.push('/repair-file/' + this.currentRepairFile.boomerang_id)
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.v-container {
  padding: 32px;
}

.title {
  font-size: 40px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.transporter-logo {
  width: 300px;
}

.v-card {
  padding: 32px;
}

.v-form p {
  margin-bottom: 16px;
}

:deep(.v-input--horizontal .v-input__append) {
  margin-right: 16px !important;
}

.error {
  color: var(--error-color);
}

.error-border {
  border: 1px solid var(--error-color);
}

.update-departure-btn {
  display: flex !important;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.date-picker {
  width: 16px;
  filter: invert(48%) sepia(0%) saturate(78%) hue-rotate(135deg) brightness(95%) contrast(83%);
}

.date-creation {
  display: flex;
  justify-items: center;
  color: #767676;
  padding: 4px;
  min-width: 125px;
}

:deep(.v-input__control) {
  min-width: 125px;
}

:deep(.checkbox-custom) .v-input__control {
  min-width: 0;
}

.date-picker,
.date-creation {
  cursor: pointer;
}

.collect-status {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: space-between;
  align-items: center;
}

.chronopost-constraint-title {
  display: flex;
  align-content: center;
  align-items: center;
  flex-direction: row;
  flex-wrap: nowrap;
}
</style>
